'use client'

import dynamic from 'next/dynamic'
import type { ComponentType } from 'react'

import { RequireAuth } from '@/auth/RequireAuth'
import type { RequireAuthGrant } from '@/routes/_hooks/requireAuthGrants'

import { ViewSkeleton } from './ViewSkeleton'

/**
 * Phase-1 page boundary: loads a migrated view client-only (`ssr: false`), so the
 * module is never imported/rendered on the server. This reproduces the SPA
 * behavior and neutralizes `window`/`localStorage` access during SSR without
 * touching the (large) view code. `FullScreenLoader` shows while the chunk loads.
 *
 * Removed page by page in It. 9 when adopting real SSR/RSC on indexable routes.
 *
 * Usage:
 *   export default clientPage(() =>
 *     import('@/components/views/ContactView').then((m) => ({ default: m.ContactView }))
 *   )
 */
export function clientPage(
  // Page views take no props; loosen variance so any view module loads cleanly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loader: () => Promise<{ default: ComponentType<any> }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<any> {
  return dynamic(loader, {
    ssr: false,
    loading: () => <ViewSkeleton />,
  })
}

/**
 * Like {@link clientPage} but wrapped in `RequireAuth`. `RequireAuth` reads the
 * route param to build the grant resource and redirects unauthenticated users to
 * `/login?from=...`.
 */
export function authedPage(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loader: () => Promise<{ default: ComponentType<any> }>,
  opts: { permission?: string; grant?: RequireAuthGrant } = {}
): ComponentType {
  const View = clientPage(loader)
  return function AuthedPage() {
    return (
      <RequireAuth permission={opts.permission} grant={opts.grant}>
        <View />
      </RequireAuth>
    )
  }
}
